import { useEffect, useCallback, useContext } from 'react'
import { withTranslation } from 'react-i18next'
import ChatService from '../../services/chat/chatService'
import AnalyticsTracking from '../analytics/analytics'
import { Spinner } from '@vp/swan'
import { initCA } from '@vp/chat-anywhere'
import chatHelper from '../../helpers/chatHelper'
import useStatusChecker from '../../hooks/useStatusChecker'
import { useBannerStatusContext } from '../../contexts/StatusContext'
import ChatWidget from './ChatWidget'
import { ChatStatusContext } from '../../contexts/ChatStatusProvider'
import { stylesheet } from './ChatWidget.scss'
import { useStyles, useUserContext } from '@vp/ubik-context'
import { useConfig } from '../../contexts/ConfigProvider'
import chatbotHelper from '../../helpers/chatbotHelper'

const LiveChatV2 = (props) => {
  const { locale, isProadvantage, tenant, t } = props
  const { featureFlags } = useUserContext()
  const {
    state: { chatStatus, gcpChatStatus },
    setState: setChatStatusFromContext,
  } = useContext(ChatStatusContext)
  const lng = locale.toLowerCase().replace('-', '_')
  const { initialData } = useBannerStatusContext()
  const { data: status, isLoading: loading } = useStatusChecker(tenant, locale, initialData)
  const config = useConfig()
  useStyles(stylesheet)
  /** Fn to check chat status from GCP */
  const chatInit = async () => {
    /** when chat is already inialized , no need to init CA again */
    if (chatStatus !== 'initial') return

    const chatService = new ChatService()
    if (chatHelper.isCAEnabled(locale.toLowerCase())) initCA()

    setChatStatusFromContext({ chatStatus: 'loading', gcpChatStatus: false })
    const chat = await chatService.getChat(tenant, locale, isProadvantage ? 'ProAdvantageChat' : 'ContactUsPage', false, config)
    const isChatEnabled = chat?.status?.isChatEnabled ?? false
    /** based on GCP response, set the chat state to preparing or offline */
    setChatStatusFromContext({ chatStatus: isChatEnabled ? 'preparing' : 'offline', gcpChatStatus: isChatEnabled })

    // For Chatbot, send a event when component is ready
    if (chatbotHelper.enableChatbot(locale, featureFlags?.['ca-chatbot-ubik'] === 'true')) {
      document.dispatchEvent(new CustomEvent('helpCenterChatWidgetReady'))
    }
  }

  /** When Start button is clicked , send event to CA */
  const startChat = () => {
    if (chatHelper.isCAEnabled(locale.toLowerCase())) {
      if (!window.$Lightning) {
        window.isChatNowClicked = true
      }
      const EVENT_NAME = 'initCAfromExternalSource'
      const data = {
        eventName: EVENT_NAME,
        timestamp: Date.now(),
        button: 'help-center-chat-now',
      }
      document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: data }))
    }
    AnalyticsTracking.trackEventWithDetails('Chat Initiated', {
      category: 'Global Chat',
      label: 'Chat help initiated',
      pageName: 'Home Page:Help Center',
      pageSection: 'Help Center',
      pageStage: 'Customer Care',
    })
  }

  /** Initial chatInit */
  useEffect(() => {
    if (loading) {
      /** When in API is in loading state */
      setChatStatusFromContext({ chatStatus: 'initial', gcpChatStatus: false })
    } else if (!status?.enabled || !status?.config?.chatDisabled) {
      chatInit()
    } else {
      /** In case chat is disabled from banner config */
      setChatStatusFromContext({ chatStatus: 'offline', gcpChatStatus: false })
    }
  }, [isProadvantage, loading, status])

  /** Send Event to CA on mount ,so that in case CA is loaded before HC,the data can be sent again */
  useEffect(() => {
    document.dispatchEvent(new CustomEvent('helpCenterCAEvent'))
  }, [])

  /** Function to set Chat status based on event received from CA */
  //  As per discussion with WFM, we should show the chat button as online always if within business hours ,
  //  they'll be managing the queue load on their side
  //  const setChatonAgentAvailabilty = useCallback(
  //     (event) => {
  //         /** when Chat window is maximized , we do not need to update the button */
  //         if (gcpChatStatus && chatStatus !== 'chatting') {
  //             const chatAvailabilty = event?.detail?.isAgentAvailable;
  //             setChatStatusFromContext((prev) => ({
  //                 ...prev,
  //                 chatStatus: chatAvailabilty ? 'online' : 'unavailable',
  //             }));
  //         }
  //     },
  //     [gcpChatStatus, chatStatus],
  // );

  /** Function to set the chat state as chatting when either chat session is coninued or window is maximized */
  const setChattingState = useCallback(() => {
    setChatStatusFromContext((prev) => ({ ...prev, chatStatus: 'chatting' }))
  }, [])

  /** Function to set the chat state as online/offline once the chat is ready or the chat window is closed */
  const setChatState = useCallback(() => {
    /** If Chat schedule Enabled ,set as online, else offline */
    setChatStatusFromContext((prev) => ({ ...prev, chatStatus: gcpChatStatus ? 'online' : 'offline' }))
  }, [gcpChatStatus])

  /** Function to set the chat state as offline based on event from CA */
  const setOfflineState = useCallback(() => {
    setChatStatusFromContext((prev) => ({ ...prev, chatStatus: 'offline' }))
  }, [])

  /** Add event handlers to events from Chat Anywhere */
  useEffect(() => {
    /** Event when chat is ready, and chat can be started */
    document.addEventListener('CAonChatInit', setChatState)
    /** Event to handle when session is continued from other pages */
    document.addEventListener('CAonSessionActive', setChattingState)
    /** Event to handle when chat window is maximized */
    document.addEventListener('CAafterMaximize', setChattingState)
    /** Event to handle when chat window is closed */
    document.addEventListener('CAafterDestroy', setChatState)
    /** Event to set the chat as offline  */
    document.addEventListener('CAchatOffline', setOfflineState)

    /** Remove event listeners on unmount */
    return () => {
      document.removeEventListener('CAonChatInit', setChatState)
      document.removeEventListener('CAonSessionActive', setChattingState)
      document.removeEventListener('CAafterMaximize', setChattingState)
      document.removeEventListener('CAafterDestroy', setChatState)
      document.removeEventListener('CAchatOffline', setOfflineState)
    }
  }, [setChattingState, setChatState])

  /** Show Loader initally when the GCP is loading  */
  if (loading || chatStatus === 'loading' || chatStatus === 'initial') {
    return (
      <Spinner
        mt={{ lg: '7', md: '7', sm: '6', xs: '6' }}
        style={{ width: '100%' }}
        accessibleText={t(`${tenant}:search_results.preloader_accessibility_label`, { lng })}
        size="standard"
      />
    )
  }

  return <ChatWidget tenant={tenant} locale={locale} startChat={startChat} t={t} />
}

export default withTranslation()(LiveChatV2)
